// SCRUM-1975 — shared US-market session clock.
//
// Extracted from the Morning Brief header (dashboard.component.ts
// computeMarketStatus) so the global top bar and the dashboard render the
// SAME open/closed state without duplicating the logic. Pure function, no
// service or network — derives the session from the America/New_York wall
// clock (pre 04:00–09:30, open 09:30–16:00, after 16:00–20:00, else closed;
// weekends closed). Holiday early-closes are not modelled here (the same as
// the original header logic).

export type MarketSessionStatus = 'pre' | 'open' | 'after' | 'closed';

export interface MarketSession {
  status: MarketSessionStatus;
  label: string;
}

export function computeMarketSession(now: Date = new Date()): MarketSession {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    weekday: 'short',
  });
  const parts = fmt.formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const wd = get('weekday');
  const h = Number.parseInt(get('hour'), 10);
  const m = Number.parseInt(get('minute'), 10);

  if (!['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(wd)) {
    return { status: 'closed', label: 'Closed' };
  }
  const mins = h * 60 + m;
  if (mins < 9 * 60 + 30) {
    return mins >= 4 * 60
      ? { status: 'pre', label: 'Pre-market' }
      : { status: 'closed', label: 'Closed' };
  }
  if (mins < 16 * 60) return { status: 'open', label: 'Open' };
  if (mins < 20 * 60) return { status: 'after', label: 'After-hours' };
  return { status: 'closed', label: 'Closed' };
}
